import { IconNames } from '@blueprintjs/icons';
import { formatDate } from '@strata/helper-functions';
import { PrimaryData, SecondaryData } from '@strata/react-grid';
import { Icon } from '@strata/react-status-icons';
import { StatusIndicator } from '@strata/react-status-indicators';
import { Tooltip } from '@strata/react-tooltip';

import { RequestSlaIndicator } from 'components/requests/RequestSlaIndicator';
import { RequestStatusDisplayTextMap, RequestStatusIndicatorMap } from 'services/requestHelpers';

const UrgencyBands = {
    critical: { text: 'Critical', icon: IconNames.ERROR, intent: Icon.Intent.ERROR },
    high: { text: 'High', icon: IconNames.WARNING_SIGN, intent: Icon.Intent.WARNING },
    normal: { text: 'Normal', icon: IconNames.EQUALS, intent: Icon.Intent.DEFAULT },
    low: { text: 'Low', icon: IconNames.CHEVRON_DOWN, intent: Icon.Intent.SECONDARY },
};

// ponytail: Rejection and Submitted By wait on agora-monitor exposing them.
export const requestsListColumns = [
    {
        accessorKey: 'requestCategoryName',
        header: 'Request Category',
        size: 160,
        cell: ({ getValue }) => <PrimaryData data={getValue()} />,
    },
    { accessorKey: 'patientName', header: 'Patient', size: 160 },
    { accessorKey: 'drugName', header: 'Drug', size: 130 },
    {
        accessorKey: 'pharmacyName',
        header: 'Pharmacy',
        size: 160,
        cell: ({ getValue, row }) => {
            const name = getValue();
            const npi = row.original.pharmacyNpi;
            return (
                <SecondaryData
                    data={name ?? npi}
                    customTooltipContent={
                        name || npi ? [name, npi && `NPI ${npi}`].filter(Boolean).join(' · ') : null
                    }
                />
            );
        },
    },
    {
        id: 'urgency',
        accessorFn: ({ urgencyScore }) => urgencyScore,
        header: 'Urgency',
        size: 96,
        cell: ({ row }) => {
            const band = UrgencyBands[row.original.urgencyBand];
            const { needsByDate } = row.original;
            return (
                band && (
                    <Icon
                        icon={band.icon}
                        intent={band.intent}
                        title={band.text}
                        tooltipContent={[
                            band.text,
                            needsByDate && `Needs by ${formatDate(needsByDate)}`,
                        ]
                            .filter(Boolean)
                            .join(' · ')}
                    />
                )
            );
        },
    },
    {
        // Keep this id so session-persisted SLA sorts from the flat-field era still match.
        id: 'slaBusinessMinutesRemaining',
        accessorFn: ({ sla }) => sla?.businessMinutesRemaining,
        header: (
            <Tooltip content="Business hours: Mon–Fri 9 AM–5 PM ET, excluding FMH holidays">
                SLA
            </Tooltip>
        ),
        size: 150,
        cell: ({ row }) => {
            const { sla } = row.original;
            return !sla ? (
                <SecondaryData data={undefined} />
            ) : (
                <RequestSlaIndicator
                    code={sla.code}
                    minutesRemaining={sla.businessMinutesRemaining}
                    clockState={sla.clockState}
                    dueUtc={sla.dueUtc}
                    resumesAtUtc={sla.resumesAtUtc}
                    band={sla.band}
                />
            );
        },
    },
    {
        accessorKey: 'state',
        header: 'Status',
        size: 160,
        cell: ({ getValue }) => {
            const indicator = RequestStatusIndicatorMap[getValue()];
            return (
                <StatusIndicator
                    status={indicator?.status ?? StatusIndicator.Status.DEFAULT}
                    message={indicator?.message ?? getValue()}
                />
            );
        },
    },
    { accessorKey: 'assignedTo', header: 'Assignee', size: 160 },
];

export const requestsListFilterCategories = [
    { key: 'requestTypeName', title: 'Request Type' },
    { key: 'requestCategoryName', title: 'Request Category' },
    {
        key: 'state',
        title: 'Status',
        valueDisplayTextMap: RequestStatusDisplayTextMap,
    },
    { key: 'pharmacyName', title: 'Pharmacy' },
    {
        key: 'urgencyBand',
        title: 'Urgency',
        valueDisplayTextMap: Object.fromEntries(
            Object.entries(UrgencyBands).map(([value, { text }]) => [value, text]),
        ),
    },
    { key: 'assignedTo', title: 'Assignee' },
];
